import { WarningOutlined } from '@ant-design/icons';
import { Input, type InputProps, Tooltip } from 'antd';
import { type ChangeEvent, type FocusEvent, useCallback, useState } from 'react';

import Colors from 'src/common/Colors';
import { CANADA_POSTAL_CODE_REGEX } from 'src/common/validation/validators';
import { cNs } from 'src/helpers';
import { Country } from 'src/types';

type ZipInputProps = InputProps & {
  country?: Country;
};

export function ZipInput({
  country = Country.Usa,
  onChange,
  onBlur,
  onFocus,
  className,
  ...restProps
}: Readonly<ZipInputProps>) {
  const [valueToSearch, setValueToSearch] = useState(String(restProps.value || restProps.defaultValue || ''));

  const [isInputFocused, setIsInputFocused] = useState(false);

  const postalCodeText = country === Country.Canada ? 'Postal code' : 'Zip code';
  const isValidValueFormat =
    country === Country.Canada ? CANADA_POSTAL_CODE_REGEX.test(valueToSearch) : valueToSearch.length === 5;

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setValueToSearch(event.target.value);

      onChange?.(event);
    },
    [onChange]
  );

  const handleInputFocus = useCallback(
    (e: FocusEvent<HTMLInputElement, Element>) => {
      setIsInputFocused(true);

      onFocus?.(e);
    },
    [onFocus]
  );

  const handleInputBlur = useCallback(
    (e: FocusEvent<HTMLInputElement, Element>) => {
      // If change it immediately then click on Google map is not going to work
      setTimeout(() => setIsInputFocused(false), 200);

      onBlur?.(e);
    },
    [onBlur]
  );

  const warningTitle = !isValidValueFormat ? `${postalCodeText} invalid format` : null;

  return (
    <Input
      {...restProps}
      onFocus={handleInputFocus}
      onBlur={handleInputBlur}
      onChange={handleChange}
      className={cNs('zip-input__container', className)}
      suffix={
        warningTitle ? (
          <Tooltip open={isInputFocused} destroyTooltipOnHide={true} title={warningTitle} placement="top">
            <WarningOutlined style={{ color: Colors.warning }} />
          </Tooltip>
        ) : (
          <WarningOutlined style={{ visibility: 'hidden' }} />
        )
      }
    />
  );
}
